import { createLogger } from '@fabric-app/utils/logging';
import { isDefined, ensureError } from '@fabric-app/utils/guards';
import { captureException } from '@fabric-app/compat/capabilities/sentry';

class BaseStorage {
  constructor(dbName) {
    this.keyCache = {};
    this.keysLoaded = false;
    this.connection = null;
    this.initializing = false;
    this.initializingPromise = null;
    this.readConnection = null;
    this.initializingRead = false;
    this.initializingReadPromise = null;
    this.MAX_RETRIES = 5;
    this.batchQueue = {};
    this.batchTimeout = null;
    this.BATCH_DELAY = 100;
    this.readBatchQueue = {};
    this.readBatchTimeout = null;
    this.READ_BATCH_DELAY = 10;
    this.performanceStats = {};
    this.PERFORMANCE_LOG_INTERVAL = 1e4;
    this.lastPerformanceLog = 0;
    this.dbName = dbName;
    this.logger = createLogger(dbName);
  }
  updatePerformanceStats(operation, time) {
    if (!isDefined(this.performanceStats[operation])) {
      this.performanceStats[operation] = {
        times: [],
        sum: 0,
        count: 0,
        high: time,
        low: time
      };
    }
    const stats = this.performanceStats[operation];
    stats.times.push(time);
    stats.sum += time;
    stats.count++;
    stats.high = Math.max(stats.high, time);
    stats.low = Math.min(stats.low, time);
    if (stats.times.length > 50) {
      const removed = stats.times.shift();
      stats.sum -= removed;
      stats.count--;
    }
    this.logPerformanceIfNeeded();
  }
  logPerformanceIfNeeded() {
    const now = Date.now();
    if (now - this.lastPerformanceLog > this.PERFORMANCE_LOG_INTERVAL) {
      this.lastPerformanceLog = now;
      for (const [operation, stats] of Object.entries(this.performanceStats)) {
        const average = stats.sum / stats.count;
        const median = this.calculateMedian(stats.times);
        this.logger.info(`PERFTIME - ${operation} - Average: ${average.toFixed(2)}ms, Median: ${median.toFixed(2)}ms, High: ${stats.high.toFixed(2)}ms, Low: ${stats.low.toFixed(2)}ms`);
      }
    }
  }
  calculateMedian(numbers) {
    const sorted = numbers.slice().sort((a, b) => a - b);
    const middle = Math.floor(sorted.length / 2);
    if (sorted.length % 2 === 0) {
      return (sorted[middle - 1] + sorted[middle]) / 2;
    }
    return sorted[middle];
  }
  async getConnection() {
    if (!this.connection) await this.ensureSingleInitialization();
    return this.connection;
  }
  async getReadConnection() {
    if (!this.readConnection) await this.ensureReadInitialization();
    return this.readConnection;
  }
  async ensureSingleInitialization() {
    this.logger.debug(`ensureSingleInitialization: Starting for ${this.dbName}`);
    if (!this.initializing) {
      this.initializing = true;
      this.initializingPromise = this.initializeConnection();
    }
    if (this.initializingPromise) {
      await this.initializingPromise;
    }
    this.logger.debug(`ensureSingleInitialization: Completed for ${this.dbName}`);
  }
  async ensureReadInitialization() {
    this.logger.debug(`ensureReadInitialization: Starting for ${this.dbName}`);
    if (!this.initializingRead) {
      this.initializingRead = true;
      this.initializingReadPromise = this.initializeReadConnection();
    }
    if (this.initializingReadPromise) {
      await this.initializingReadPromise;
    }
    this.logger.debug(`ensureReadInitialization: Completed for ${this.dbName}`);
  }
  async withErrorHandling(operation, func) {
    const start = Date.now();
    try {
      const result = await func();
      const time = Date.now() - start;
      this.updatePerformanceStats(operation, time);
      if (time > this.performanceStats[operation].sum / this.performanceStats[operation].count) {
        this.logger.warn(`${operation} took longer than average: ${time.toFixed(2)}ms`);
      }
      return result;
    } catch (error) {
      this.logger.warn(`${operation} failed:`, error);
      const isConnected = await this.checkConnection();
      if (!isConnected) {
        this.logger.info("Attempting to reconnect...");
        await this.reconnect();
        this.logger.debug(`${operation}: Retrying operation after reconnection`);
        return func();
      }
      throw ensureError(error);
    }
  }
  async *withErrorHandlingIterator(operationName, operation) {
    const start = Date.now();
    try {
      yield* operation();
      const time = Date.now() - start;
      this.updatePerformanceStats(operationName, time);
      if (time > this.performanceStats[operationName].sum / this.performanceStats[operationName].count) {
        this.logger.warn(`${operationName} took longer than average: ${time.toFixed(2)}ms`);
      }
    } catch (error) {
      this.logger.warn(`${operationName} failed:`, error);
      const isConnected = await this.checkConnection();
      if (!isConnected) {
        this.logger.info("Attempting to reconnect...");
        await this.reconnect();
        this.logger.debug(`withErrorHandlingIterator: Retrying ${operationName} after reconnection`);
        const retryStart = Date.now();
        yield* operation();
        const retryTime = Date.now() - retryStart;
        this.updatePerformanceStats(`${operationName}_retry`, retryTime);
      } else {
        throw ensureError(error);
      }
    }
  }
  async loadKeysToMemory(storeName) {
    this.keyCache[storeName] = new Set(await this.keys(storeName));
    this.keysLoaded = true;
  }
  keyExists(storeName, key) {
    if (!this.keysLoaded) return true;
    return this.keyCache[storeName]?.has(key) || false;
  }
  addKeyToCache(storeName, key) {
    if (!isDefined(this.keyCache[storeName])) {
      this.keyCache[storeName] = /* @__PURE__ */ new Set();
    }
    this.keyCache[storeName].add(key);
  }
  removeKeyFromCache(storeName, key) {
    if (isDefined(this.keyCache[storeName])) {
      this.keyCache[storeName].delete(key);
    }
  }
  clearKeyCache(storeName) {
    delete this.keyCache[storeName];
  }
  async getItem(storeName, key) {
    if (!this.keyExists(storeName, key)) {
      return null;
    }
    this.logger.debug(`getItem: Queueing item for batch read. Store: ${storeName}, Key: ${key}`);
    if (!isDefined(this.readBatchQueue[storeName])) {
      this.readBatchQueue[storeName] = /* @__PURE__ */ new Set();
    }
    this.readBatchQueue[storeName].add(key);
    if (!this.readBatchTimeout) {
      this.logger.debug(`getItem: Setting batch read flush timeout`);
      this.readBatchTimeout = setTimeout(() => {
        this.flushReadBatch().catch((e) => {
          captureException(ensureError(e), {
            level: "error"
          });
        });
      }, this.READ_BATCH_DELAY);
    }
    this.addGetItemToReadCache(storeName, key);
    return new Promise((resolve) => {
      const checkResult = async () => {
        try {
          const result = await this.getItemImpl(storeName, key);
          if (result !== void 0) {
            resolve(result);
          } else {
            setTimeout(checkResult, 15);
          }
        } catch (error) {
          captureException(ensureError(error), {
            level: "error"
          });
        }
      };
      setTimeout(checkResult, this.READ_BATCH_DELAY + 1);
    });
  }
  setItem(storeName, key, value) {
    this.addKeyToCache(storeName, key);
    this.logger.debug(`setItem: Queueing item for batch. Store: ${storeName}, Key: ${key}`);
    if (!isDefined(this.batchQueue[storeName])) {
      this.batchQueue[storeName] = /* @__PURE__ */ new Map();
    }
    this.batchQueue[storeName].set(key, value);
    if (!this.batchTimeout) {
      this.logger.debug(`setItem: Setting batch flush timeout`);
      this.batchTimeout = setTimeout(() => {
        this.flushBatch().catch((e) => {
          captureException(ensureError(e), {
            level: "error"
          });
        });
      }, this.BATCH_DELAY);
    }
    return Promise.resolve();
  }
  async flushBatch() {
    this.logger.debug(`flushBatch: Starting batch flush`);
    const batchesToProcess = {
      ...this.batchQueue
    };
    this.batchQueue = {};
    this.batchTimeout = null;
    await this.withErrorHandling("flushBatch", () => this.flushBatchImpl(batchesToProcess));
  }
  async removeItem(storeName, key) {
    this.removeKeyFromCache(storeName, key);
    this.logger.debug(`removeItem: Attempting to remove item. Store: ${storeName}, Key: ${key}`);
    return this.withErrorHandling("removeItem", () => this.removeItemImpl(storeName, key));
  }
  async clear(storeName) {
    this.clearKeyCache(storeName);
    this.logger.debug(`clear: Attempting to clear store: ${storeName}`);
    return this.withErrorHandling("clear", () => this.clearImpl(storeName));
  }
  async keys(storeName) {
    this.logger.debug(`keys: Retrieving keys for store: ${storeName}`);
    return this.withErrorHandling("keys", () => this.keysImpl(storeName));
  }
  async *getAllItemsIterator(storeName) {
    this.logger.debug(`getAllItemsIterator: Starting retrieval of all items from store: ${storeName}`);
    yield* this.withErrorHandlingIterator("getAllItemsIterator", () => this.getAllItemsIteratorImpl(storeName));
  }
  async getAllItems(storeName) {
    this.logger.debug(`getAllItems: Starting retrieval of all items from store: ${storeName}`);
    return this.withErrorHandling("getAllItems", () => this.getAllItemsImpl(storeName));
  }
  async flushBatchImpl(batchesToProcess) {
    for (const [storeName, items] of Object.entries(batchesToProcess)) {
      await this.setItemBatchImpl(storeName, items);
      for (const key of items.keys()) {
        this.addKeyToCache(storeName, key);
      }
    }
  }
  async flushReadBatch() {
    this.logger.debug(`flushReadBatch: Starting batch read flush`);
    const batchesToProcess = {
      ...this.readBatchQueue
    };
    this.readBatchQueue = {};
    this.readBatchTimeout = null;
    await this.withErrorHandling("flushReadBatch", () => this.flushReadBatchImpl(batchesToProcess));
  }
}

export { BaseStorage };
//# sourceMappingURL=BaseStorage.js.map
